import { Button, Dropdown, Flex, FormStatus, GlobalError, H1, P, Skeleton, Space } from '@dnb/eufemia';
import { edit as EditIcon, launch as LaunchIcon } from '@dnb/eufemia/icons';
import { TrackRoute } from '@portals/shared-frontend/components';
import { useCallback, useEffect, useMemo } from 'react';
import { generatePath, Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';

import HeroPage from '@/components/HeroPage';
import HeroTopContent from '@/components/HeroPage/HeroTopContent';
import { HeroPageGuide } from '@/components/HeroPageGuide';
import { useBackend } from '@/hooks/useBackend';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import { McpReleaseResponse } from '@/openapi/schema';
import { NotFoundPage } from '@/pages/NotFoundPage';
import ApiStageBadge from '@/pages/profile/components/ApiStageBadge';

import * as styles from '@/pages/api-documentation/styles/Api.styles';

const DUMMY_GUIDE = `## Loading

Content is loading...`;

function getLatestRelease(releases: McpReleaseResponse[]) {
  return [...releases].sort((a, b) => (b.deployedAt ?? 0) - (a.deployedAt ?? 0))[0];
}

export default function McpDocumentation() {
  const { featureFlags } = useFeatureFlags();
  const isMcpPagesEnabled = featureFlags.ENABLE_MCP_PAGES;
  const {
    slug,
    api: apiParam,
    environment: envParam,
    version: versionParam,
  } = useParams<{
    slug: string;
    api: string;
    environment: string;
    version: string;
  }>();
  const location = useLocation();
  const navigate = useNavigate();

  const mcpSlug = apiParam ?? slug;
  const hasRequiredPathParams = apiParam && envParam && versionParam;

  const { data: mcpData, isLoading: isLoadingMcp } = useBackend(
    '/mcp/{mcpSlug}',
    isMcpPagesEnabled && mcpSlug
      ? {
          params: {
            path: {
              mcpSlug,
            },
          },
        }
      : null,
  );

  const selectedEnvironment = useMemo(() => {
    if (!mcpData?.environments?.length || !envParam) {
      return undefined;
    }

    if (envParam === '@default') {
      return mcpData.environments[0];
    }

    return mcpData.environments.find((environment) => environment.slug === envParam);
  }, [mcpData?.environments, envParam]);

  const environmentOptions = useMemo(
    () =>
      mcpData?.environments.map((environment) => ({
        key: environment.slug,
        content: environment.name,
      })) ?? [],
    [mcpData?.environments],
  );

  const { data: releases, isLoading: isLoadingReleases } = useBackend(
    '/mcp/{mcpSlug}/environments/{environmentSlug}/releases',
    isMcpPagesEnabled && mcpSlug && selectedEnvironment
      ? {
          params: {
            path: {
              mcpSlug,
              environmentSlug: selectedEnvironment.slug,
            },
          },
        }
      : null,
  );

  const releaseOptions = useMemo(
    () =>
      releases?.map((release) => ({
        key: release.version,
        content: release.name,
        selected_value: `${release.name} (${release.version})`,
      })) ?? [],
    [releases],
  );

  const selectedRelease = useMemo(() => {
    if (!releases || releases.length === 0 || !versionParam) return undefined;

    if (versionParam === '@latest') {
      return getLatestRelease(releases);
    }

    return releases.find((release) => release.version === versionParam);
  }, [releases, versionParam]);

  const { data: releaseData, isLoading: isLoadingReleaseData } = useBackend(
    '/mcp/{mcpSlug}/environments/{environmentSlug}/releases/{version}',
    isMcpPagesEnabled && mcpSlug && selectedEnvironment && selectedRelease
      ? {
          params: {
            path: {
              mcpSlug,
              environmentSlug: selectedEnvironment.slug,
              version: selectedRelease.version,
            },
          },
        }
      : null,
  );

  const isLoading = isLoadingMcp || isLoadingReleases || isLoadingReleaseData;
  const guideContent = releaseData?.guide ?? '';

  const doNavigate = useCallback(
    ({ env, release, replace }: { env?: string; release?: string; replace?: boolean }) => {
      if (!mcpSlug || !envParam || !versionParam) {
        return;
      }

      const path = generatePath('/mcp-servers/:api/:environment/:version/guide', {
        api: mcpSlug,
        environment: env ?? envParam,
        version: release ?? versionParam,
      });

      let url = path;
      if (replace) {
        url += location.search || '';
        url += location.hash || '';
      }

      navigate(url, { replace });
    },
    [mcpSlug, envParam, versionParam, navigate],
  );

  useEffect(() => {
    if (!mcpSlug || hasRequiredPathParams) {
      return;
    }

    navigate(`/mcp-servers/${mcpSlug}/@default/@latest/guide`, { replace: true });
  }, [mcpSlug, hasRequiredPathParams, navigate]);

  useEffect(() => {
    if (!apiParam || !envParam || !versionParam || !location.pathname.startsWith('/mcp/')) {
      return;
    }

    const path = `/mcp-servers/${apiParam}/${envParam}/${versionParam}/guide`;
    if (location.pathname !== path) {
      navigate(`${path}${location.search}${location.hash}`, { replace: true });
    }
  }, [apiParam, envParam, versionParam, location.pathname, location.search, location.hash, navigate]);

  useEffect(() => {
    if (!mcpData || envParam !== '@default') return;

    const defaultEnv = mcpData.environments[0];
    if (!defaultEnv) return;

    doNavigate({ env: defaultEnv.slug, replace: true });
  }, [mcpData, envParam]);

  useEffect(() => {
    if (!releases || releases.length === 0 || versionParam !== '@latest') return;

    const latestRelease = getLatestRelease(releases);
    if (!latestRelease) return;

    doNavigate({ release: latestRelease.version, replace: true });
  }, [releases, versionParam]);

  if (!isMcpPagesEnabled) {
    return <NotFoundPage />;
  }

  return (
    <div className="dark-mode">
      <HeroPage
        banner="documentation"
        heroLeftComponent={
          mcpData && (
            <styles.HeroHeaderContent>
              <styles.HeroTitleRow>
                <H1 size="x-large">{mcpData.name}</H1>
                <ApiStageBadge stage={mcpData.stage} />
              </styles.HeroTitleRow>
              <P weight="medium">{mcpData.description}</P>
              {mcpData.stage === 'DEPRECATED' && (
                <FormStatus state="warn" text="This MCP server is deprecated." variant="outlined" />
              )}
              <styles.StyledButtonWrapper>
                {mcpData.isOwner && mcpData.ownerTeamId && mcpData.appId && (
                  <Button
                    element={RouterLink}
                    icon={<LaunchIcon />}
                    target="_blank"
                    to={`/team/${mcpData.ownerTeamId}/application/${mcpData.appId}/api/${mcpData.id}`}
                    tooltip="Manage visibility, access and more."
                    variant="secondary"
                  >
                    Manage MCP
                  </Button>
                )}
              </styles.StyledButtonWrapper>
            </styles.HeroHeaderContent>
          )
        }
        noTopMargin
        skeleton={isLoading}
        title={mcpData?.name}
      >
        {mcpData && (
          <>
            <HeroTopContent noBottomPadding>
              <TrackRoute
                name={mcpData.name}
                path={`/mcp-servers/${mcpData.slug}/@default/@latest/guide`}
                type="mcp-doc"
              />
              <div className="flex flex-wrap items-end gap-4 pb-4">
                {environmentOptions.length > 0 && (
                  <Dropdown
                    data={environmentOptions}
                    label="Environment"
                    label_direction="vertical"
                    on_change={({ data }) => {
                      doNavigate({ env: data.key, release: '@latest' });
                    }}
                    title="Select environment"
                    value={environmentOptions.findIndex((option) => option.key === selectedEnvironment?.slug)}
                  />
                )}
                {releaseOptions.length > 0 && (
                  <Dropdown
                    data={releaseOptions}
                    label="Version"
                    label_direction="vertical"
                    on_change={({ data }) => {
                      doNavigate({ release: data.key });
                    }}
                    title="Select version"
                    value={releaseOptions.findIndex((option) => option.key === selectedRelease?.version)}
                  />
                )}
              </div>
            </HeroTopContent>
            {mcpData.isOwner && selectedEnvironment && selectedRelease && (
              <Flex.Horizontal bottom="small" justify="flex-end">
                <Button
                  element={RouterLink}
                  icon={<EditIcon />}
                  icon_position="left"
                  to={`/mcp-servers/${mcpSlug}/${selectedEnvironment.slug}/${selectedRelease.version}/guide/edit`}
                  variant="tertiary"
                >
                  Edit guide
                </Button>
              </Flex.Horizontal>
            )}
            {guideContent && <HeroPageGuide content={guideContent} />}
          </>
        )}
        {!isLoading && !mcpData && (
          <Space bottom={10}>
            <GlobalError title="MCP not found" />
          </Space>
        )}
        {!isLoading && mcpData && !guideContent && (
          <Space bottom={10}>
            <GlobalError title="Documentation not available" />
          </Space>
        )}
        {isLoading && (
          <Skeleton show>
            <HeroPageGuide content={DUMMY_GUIDE} loading />
          </Skeleton>
        )}
      </HeroPage>
    </div>
  );
}
